import React, { useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Plus, Edit2, Archive, Calendar as CalendarIcon, ChevronDown, Mail, MessageCircle } from "lucide-react";

import { ListPageLayout } from "@/components/cadastro/ListPageLayout";
import { DataTable, ColumnDef } from "@/components/cadastro/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

// Types — fonte única: ClienteCache (tabela `clientes` no Supabase)
import {
  ClienteCache,
  type Cliente,
  type ClienteTipo as Tipo,
  type ClienteEndereco as Endereco,
  type ClienteContato as ContatoPF,
} from "@/services/clienteCache";

// Helpers
function formatDate(d?: string) {
  try {
    return d ? format(new Date(d), "dd/MM/yyyy", { locale: ptBR }) : "—";
  } catch {
    return "—";
  }
}
function maskCPF(v?: string) {
  if (!v) return "—";
  return v
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}
function maskCNPJ(v?: string) {
  if (!v) return "—";
  return v
    .replace(/\D/g, "")
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
}

// DateRange Picker
function DateRangePicker({ range, onChange, label }: { range: DateRange | undefined; onChange: (r: DateRange | undefined) => void; label?: string }) {
  const [open, setOpen] = useState(false);
  const text = range?.from
    ? range.to
      ? `${format(range.from, "dd/MM/yyyy")} - ${format(range.to, "dd/MM/yyyy")}`
      : `${format(range.from, "dd/MM/yyyy")} - …`
    : label || "Período";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-[260px] justify-start text-left font-normal">
          <CalendarIcon className="mr-2 h-4 w-4" />
          {text}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 sm:w-[620px]" align="start">
        <Calendar
          mode="range"
          selected={range}
          onSelect={onChange}
          numberOfMonths={2}
          pagedNavigation
          fixedWeeks
          locale={ptBR}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
        <div className="flex justify-end gap-2 border-t p-2">
          <Button variant="ghost" size="sm" className="h-11 sm:h-9" onClick={() => onChange(undefined)} disabled={!range?.from && !range?.to}>
            Limpar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Clientes() {
  const [loading] = useState(false);

  // Fonte única: clientes reais (Supabase via ClienteCache)
  const [clientes, setClientes] = useState<Cliente[]>(() => ClienteCache.getClientes());

  useEffect(() => {
    document.title = "Clientes | Cadastro";
    setClientes(ClienteCache.getClientes());
  }, []);

  // Filters
  const [search, setSearch] = useState("");
  const [tipo, setTipo] = useState<"todos" | Tipo>("todos");
  const [cidade, setCidade] = useState("");

  const filtered = useMemo(() => {
    let data = [...clientes];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter((c) =>
        `${c.tipo} ${c.nome || ""} ${c.nomeFantasia || ""} ${c.razaoSocial || ""} ${c.cpf || ""} ${c.cnpj || ""} ${c.emails[0] || ""} ${c.telefones[0] || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    if (tipo !== "todos") data = data.filter((c) => c.tipo === tipo);
    if (cidade) data = data.filter((c) => (c.endereco.cidade || "").toLowerCase().includes(cidade.toLowerCase()));
    return data;
  }, [clientes, search, tipo, cidade]);

  // Add/Edit
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Cliente | null>(null);

  function openAdd() {
    setEditing(null);
    setSelectorOpen(true);
  }

  function handleSave(cliente: Cliente) {
    // Persistência real (Supabase via ClienteCache, write-through)
    const exists = cliente.id && ClienteCache.getClienteById(cliente.id);
    if (exists) {
      ClienteCache.updateCliente(cliente);
    } else {
      const { id, createdAt, updatedAt, ...rest } = cliente;
      ClienteCache.addCliente(rest);
    }
    setClientes(ClienteCache.getClientes());
    setEditOpen(false);
    setSelectorOpen(false);
    toast({ title: "Cliente salvo" });
  }

  function archive(cliente: Cliente) {
    ClienteCache.deleteCliente(cliente.id);
    setClientes(ClienteCache.getClientes());
    toast({ title: "Cliente arquivado", description: (cliente.nome || cliente.nomeFantasia) ?? "" });
  }

  // Columns
  type Row = Cliente & { telefonePrincipal?: string; emailPrincipal?: string };

  const columns: ColumnDef<Row>[] = [
    { key: "tipo", header: "Tipo", render: (v) => <Badge className={cn("rounded-full", v === "PF" ? "bg-primary/10 text-primary" : "bg-secondary/50 text-foreground")}>{v === "PF" ? "Física" : "Jurídica"}</Badge>, className: "min-w-[120px]" },
    { key: "nome", header: "Nome / Nome Fantasia", render: (_v, r) => <span className="font-medium">{r.tipo === "PF" ? r.nome : r.nomeFantasia}</span>, className: "min-w-[220px]" },
    { key: "cpf", header: "CPF/CNPJ", render: (_v, r) => r.tipo === "PF" ? maskCPF(r.cpf) : maskCNPJ(r.cnpj), className: "min-w-[160px] whitespace-nowrap" },
    { key: "telefonePrincipal", header: "Telefone principal", render: (_v, r) => {
      const phone = r.telefones?.[0];
      if (!phone) return <span>—</span>;
      const digits = phone.replace(/\D/g, "");
      return (
        <div className="flex items-center gap-2">
          <a 
            href={`whatsapp://send?phone=55${digits}&text=Olá!`}
            className="inline-flex min-h-11 items-center gap-1 py-2 transition-opacity hover:opacity-80 cursor-pointer sm:min-h-0 sm:py-0"
            title="Abrir no WhatsApp"
          >
            <MessageCircle className="h-4 w-4 text-green-600 hover:text-green-700" />
            <span className="text-sm text-foreground">{phone}</span>
          </a>
        </div>
      );
    }, className: "min-w-[200px]" },
    { key: "emailPrincipal", header: "E-mail principal", render: (_v, r) => {
      const email = r.emails?.[0];
      return email ? <a href={`mailto:${email}`} className="inline-flex min-h-11 items-center py-2 underline underline-offset-2 sm:min-h-0 sm:py-0">{email}</a> : <span>—</span>;
    }, className: "min-w-[220px]" },
    { key: "endereco", header: "Cidade/UF", render: (_v, r) => (
      r.endereco?.cidade ? <span>{r.endereco.cidade}/{r.endereco.uf || "—"}</span> : <span>—</span>
    ), className: "min-w-[160px] whitespace-nowrap" },
    { key: "contatos", header: "Qtd. contatos", render: (_v, r) => r.tipo === "PJ" ? <span>{r.contatos?.length || 0}</span> : <span>—</span>, className: "min-w-[120px]" },
    { key: "updatedAt", header: "Atualizado em", render: (v) => formatDate(v as string), className: "min-w-[140px]" },
    {
      key: "id",
      header: "Ações",
      className: "w-[120px] text-right",
      render: (_v, r) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" aria-label="Editar" onClick={(e) => { e.stopPropagation?.(); setEditing(r); setEditOpen(true); }}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" aria-label="Arquivar" onClick={(e) => { e.stopPropagation?.(); archive(r); }}>
            <Archive className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Filters UI
  const filters = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1 rounded-md border p-1">
        <Button variant={tipo === "todos" ? "default" : "ghost"} size="sm" className="h-11 sm:h-9" onClick={() => setTipo("todos")}>Todos</Button>
        <Button variant={tipo === "PF" ? "default" : "ghost"} size="sm" className="h-11 sm:h-9" onClick={() => setTipo("PF")}>Física</Button>
        <Button variant={tipo === "PJ" ? "default" : "ghost"} size="sm" className="h-11 sm:h-9" onClick={() => setTipo("PJ")}>Jurídica</Button>
      </div>
      <Input value={cidade} onChange={(e) => setCidade(e.target.value)} placeholder="Cidade" className="w-[180px]" />
    </div>
  );

  // Empty state
  const empty = (
    <div className="mt-10 grid place-items-center rounded-lg border border-dashed p-12 text-center">
      <div className="max-w-lg space-y-3">
        <p className="text-lg font-medium">Sem clientes ainda</p>
        <p className="text-muted-foreground">Clique em \"+ Adicionar\" para cadastrar seu primeiro cliente.</p>
        <Button onClick={openAdd}><Plus className="mr-2 h-4 w-4" /> Adicionar</Button>
      </div>
    </div>
  );

  return (
    <section className="flex-1">
      <ListPageLayout
        title="Clientes"
        search={search}
        onSearchChange={setSearch}
        onAdd={openAdd}
        filters={filters}
        searchPlaceholder="Buscar por nome/razão, CPF/CNPJ, e-mail, telefone…"
      >
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-[44px] w-full" />
            <Skeleton className="h-[280px] w-full" />
          </div>
        ) : filtered.length === 0 ? (
          empty
        ) : (
          <DataTable data={filtered} setData={setClientes} columns={columns} pageSize={8} dense />
        )}
      </ListPageLayout>

      {/* Selector Modal */}
      <Dialog open={selectorOpen} onOpenChange={setSelectorOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Cliente</DialogTitle>
            <DialogDescription>Selecione o tipo de cliente para continuar.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={() => { setEditing({ id: "", tipo: "PF", nome: "", telefones: [""], emails: [""], endereco: {}, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }); setEditOpen(true); }}>Cliente Física</Button>
            <Button onClick={() => { setEditing({ id: "", tipo: "PJ", nomeFantasia: "", razaoSocial: "", telefones: [""], emails: [""], endereco: {}, contatos: [], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }); setEditOpen(true); }}>Cliente Jurídica</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectorOpen(false)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit/Create Modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editar Cliente" : "Cadastrar Cliente"}</DialogTitle>
          </DialogHeader>
          {editing && (editing.tipo === "PF" ? (
            <ClientePFForm initial={editing} onCancel={() => setEditOpen(false)} onSave={handleSave} />
          ) : (
            <ClientePJForm initial={editing} onCancel={() => setEditOpen(false)} onSave={handleSave} />
          ))}
        </DialogContent>
      </Dialog>
    </section>
  );
}

// PF Form
function ClientePFForm({ initial, onSave, onCancel }: { initial: Cliente; onSave: (c: Cliente) => void; onCancel: () => void }) {
  const [form, setForm] = useState<Cliente>(initial);
  function addTelefone() { setForm((f) => ({ ...f, telefones: [...(f.telefones || []), ""] })); }
  function addEmail() { setForm((f) => ({ ...f, emails: [...(f.emails || []), ""] })); }

  function validate(): string | null {
    if (!form.nome?.trim()) return "Nome é obrigatório";
    if (form.emails.some((e) => e && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e))) return "E-mail inválido";
    if (form.endereco.cep && !/^\d{5}-?\d{3}$/.test(form.endereco.cep)) return "CEP inválido";
    return null;
  }
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm">Nome *</label>
          <Input value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome completo" />
        </div>
        <div>
          <label className="text-sm">CPF</label>
          <Input value={form.cpf || ""} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" />
        </div>
        <div className="col-span-2">
          <label className="text-sm">Telefones</label>
          <div className="space-y-2">
            {form.telefones.map((t, i) => (
              <Input key={i} value={t} onChange={(e) => setForm({ ...form, telefones: form.telefones.map((x, xi) => (xi === i ? e.target.value : x)) })} placeholder="(00) 00000-0000" />
            ))}
            <Button variant="secondary" size="sm" className="h-11 sm:h-9" onClick={addTelefone}>+ Adicionar telefone</Button>
          </div>
        </div>
        <div className="col-span-2">
          <label className="text-sm">E-mails</label>
          <div className="space-y-2">
            {form.emails.map((t, i) => (
              <Input key={i} value={t} onChange={(e) => setForm({ ...form, emails: form.emails.map((x, xi) => (xi === i ? e.target.value : x)) })} placeholder="email@exemplo.com" />
            ))}
            <Button variant="secondary" size="sm" className="h-11 sm:h-9" onClick={addEmail}>+ Adicionar e-mail</Button>
          </div>
        </div>
        <EnderecoFields form={form} setForm={setForm} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={() => { const err = validate(); if (err) return toast({ title: err, variant: "destructive" }); onSave({ ...form, tipo: "PF" }); }}>Salvar</Button>
      </div>
    </div>
  );
}

// PJ Form
function ClientePJForm({ initial, onSave, onCancel }: { initial: Cliente; onSave: (c: Cliente) => void; onCancel: () => void }) {
  const [form, setForm] = useState<Cliente>(initial);
  const [openContato, setOpenContato] = useState(false);
  const [novoContato, setNovoContato] = useState<ContatoPF>({ id: "", nome: "" });

  function addTelefone() { setForm((f) => ({ ...f, telefones: [...(f.telefones || []), ""] })); }
  function addEmail() { setForm((f) => ({ ...f, emails: [...(f.emails || []), ""] })); }

  function validate(): string | null {
    if (!form.nomeFantasia?.trim()) return "Nome Fantasia é obrigatório";
    if (!form.cnpj?.trim()) return "CNPJ é obrigatório";
    if (form.emails.some((e) => e && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e))) return "E-mail inválido";
    if (form.endereco.cep && !/^\d{5}-?\d{3}$/.test(form.endereco.cep)) return "CEP inválido";
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm">Nome Fantasia *</label>
          <Input value={form.nomeFantasia || ""} onChange={(e) => setForm({ ...form, nomeFantasia: e.target.value })} placeholder="Empresa LTDA" />
        </div>
        <div>
          <label className="text-sm">Razão Social</label>
          <Input value={form.razaoSocial || ""} onChange={(e) => setForm({ ...form, razaoSocial: e.target.value })} placeholder="Razão Social" />
        </div>
        <div>
          <label className="text-sm">CNPJ *</label>
          <Input value={form.cnpj || ""} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} placeholder="00.000.000/0001-00" />
        </div>
        <div />
        <div className="col-span-2">
          <label className="text-sm">Telefones</label>
          <div className="space-y-2">
            {form.telefones.map((t, i) => (
              <Input key={i} value={t} onChange={(e) => setForm({ ...form, telefones: form.telefones.map((x, xi) => (xi === i ? e.target.value : x)) })} placeholder="(00) 0000-0000" />
            ))}
            <Button variant="secondary" size="sm" className="h-11 sm:h-9" onClick={addTelefone}>+ Adicionar telefone</Button>
          </div>
        </div>
        <div className="col-span-2">
          <label className="text-sm">E-mails</label>
          <div className="space-y-2">
            {form.emails.map((t, i) => (
              <Input key={i} value={t} onChange={(e) => setForm({ ...form, emails: form.emails.map((x, xi) => (xi === i ? e.target.value : x)) })} placeholder="email@empresa.com" />
            ))}
            <Button variant="secondary" size="sm" className="h-11 sm:h-9" onClick={addEmail}>+ Adicionar e-mail</Button>
          </div>
        </div>

        <EnderecoFields form={form} setForm={setForm} />

        <div className="col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <label className="text-sm">Contatos (PF)</label>
            <Button size="sm" className="h-11 sm:h-9" variant="secondary" onClick={() => setOpenContato(true)}>Adicionar Contato</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(form.contatos || []).map((c) => (
              <span key={c.id} className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-sm">
                {c.nome}
                {c.email && <a href={`mailto:${c.email}`} aria-label="Enviar e-mail"><Mail className="h-4 w-4" /></a>}
                {c.telefone && <a href={`whatsapp://send?phone=55${c.telefone.replace(/\D/g, "")}&text=Olá!`} aria-label="WhatsApp"><MessageCircle className="h-4 w-4 text-green-600 hover:text-green-700" /></a>}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={() => { const err = validate(); if (err) return toast({ title: err, variant: "destructive" }); onSave({ ...form, tipo: "PJ" }); }}>Salvar</Button>
      </div>

      <Dialog open={openContato} onOpenChange={setOpenContato}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Contato</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Nome" value={novoContato.nome} onChange={(e) => setNovoContato({ ...novoContato, nome: e.target.value })} />
            <Input placeholder="Telefone" value={novoContato.telefone || ""} onChange={(e) => setNovoContato({ ...novoContato, telefone: e.target.value })} />
            <Input placeholder="E-mail" value={novoContato.email || ""} onChange={(e) => setNovoContato({ ...novoContato, email: e.target.value })} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpenContato(false)}>Cancelar</Button>
              <Button onClick={() => {
                if (!novoContato.nome.trim()) return toast({ title: "Informe o nome do contato", variant: "destructive" });
                const contato: ContatoPF = { ...novoContato, id: String(Date.now()) };
                setForm((f) => ({ ...f, contatos: [...(f.contatos || []), contato] }));
                setNovoContato({ id: "", nome: "" });
                setOpenContato(false);
              }}>Adicionar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Shared address fields
function EnderecoFields({ form, setForm }: { form: Cliente; setForm: React.Dispatch<React.SetStateAction<Cliente>> }) {
  return (
    <>
      <div>
        <label className="text-sm">Logradouro</label>
        <Input value={form.endereco.logradouro || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, logradouro: e.target.value } })} />
      </div>
      <div>
        <label className="text-sm">Número</label>
        <Input value={form.endereco.numero || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, numero: e.target.value } })} />
      </div>
      <div>
        <label className="text-sm">Complemento</label>
        <Input value={form.endereco.complemento || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, complemento: e.target.value } })} />
      </div>
      <div>
        <label className="text-sm">Bairro</label>
        <Input value={form.endereco.bairro || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, bairro: e.target.value } })} />
      </div>
      <div>
        <label className="text-sm">Cidade</label>
        <Input value={form.endereco.cidade || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, cidade: e.target.value } })} />
      </div>
      <div>
        <label className="text-sm">UF</label>
        <Input value={form.endereco.uf || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, uf: e.target.value } })} />
      </div>
      <div className="col-span-2">
        <label className="text-sm">CEP</label>
        <Input value={form.endereco.cep || ""} onChange={(e) => setForm({ ...form, endereco: { ...form.endereco, cep: e.target.value } })} />
      </div>
    </>
  );
}
